
import { useState, useEffect } from 'react';
import { logError } from '@/utils/errorHandling';

interface BackendStatus {
  isBackendError: boolean;
  isSlowResponse: boolean;
  showBackendErrorModal: boolean;
}

interface RequestMetrics {
  startTime: number;
  isCompleted: boolean;
  hasError: boolean;
}

export const useBackendStatus = () => {
  const [backendStatus, setBackendStatus] = useState<BackendStatus>({
    isBackendError: false,
    isSlowResponse: false,
    showBackendErrorModal: false,
  });

  const [requestMetrics, setRequestMetrics] = useState<RequestMetrics[]>([]);

  // Track request failures and slow responses
  const trackRequest = (requestPromise: Promise<any>, requestType: string = 'api') => {
    const startTime = Date.now();
    const requestId = requestMetrics.length;

    console.log(`🔄 Tracking ${requestType} request...`);

    // Add new request to metrics
    setRequestMetrics(prev => [...prev, {
      startTime,
      isCompleted: false,
      hasError: false
    }]);

    requestPromise
      .then((result) => {
        const endTime = Date.now();
        const duration = endTime - startTime;

        console.log(`✅ ${requestType} request completed in ${duration}ms`);

        // Update request as completed
        setRequestMetrics(prev => prev.map((metric, index) => 
          index === requestId 
            ? { ...metric, isCompleted: true }
            : metric
        ));

        // Check if response is slow (> 5 seconds)
        if (duration > 5000) {
          console.log(`🐌 Slow ${requestType} response detected: ${duration}ms`);
          setBackendStatus(prev => ({
            ...prev,
            isSlowResponse: true,
            showBackendErrorModal: true
          }));
        }

        return result;
      })
      .catch((error) => {
        const endTime = Date.now();
        const duration = endTime - startTime;

        console.log(`❌ ${requestType} request failed after ${duration}ms:`, error);

        // Update request as completed with error
        setRequestMetrics(prev => prev.map((metric, index) => 
          index === requestId 
            ? { ...metric, isCompleted: true, hasError: true }
            : metric
        ));

        // Log the error
        logError(error, `Backend ${requestType} request failed`);

        // Check for backend errors (5xx, network errors, timeouts)
        const isBackendError = 
          error.response?.status >= 500 ||
          error.code === 'NETWORK_ERROR' ||
          error.code === 'ECONNABORTED' ||
          error.message?.includes('timeout') ||
          error.message?.includes('fetch') ||
          error.name === 'NetworkError' ||
          !error.response;

        if (isBackendError) {
          console.log(`🚨 Backend error detected for ${requestType}:`, error);
          setBackendStatus(prev => ({
            ...prev,
            isBackendError: true,
            showBackendErrorModal: true
          }));
        }

        throw error;
      });

    return requestPromise;
  };

  // Function to manually trigger the modal for testing
  const triggerBackendErrorModal = (isSlowResponse = false) => {
    console.log('🧪 Manually triggering backend error modal for testing');
    setBackendStatus({
      isBackendError: !isSlowResponse,
      isSlowResponse,
      showBackendErrorModal: true
    });
  };

  // Check for multiple failures in recent timeframe
  useEffect(() => {
    const checkForRepeatedFailures = () => {
      const now = Date.now();
      const recentWindow = 60000; // 1 minute
      
      const recentRequests = requestMetrics.filter(
        metric => now - metric.startTime < recentWindow && metric.isCompleted
      );

      if (recentRequests.length >= 3) {
        const failedRequests = recentRequests.filter(metric => metric.hasError);
        const failureRate = failedRequests.length / recentRequests.length;

        // If more than 50% of recent requests failed
        if (failureRate > 0.5) {
          console.log(`🚨 High failure rate detected: ${(failureRate * 100).toFixed(1)}%`);
          setBackendStatus(prev => ({
            ...prev,
            isBackendError: true,
            showBackendErrorModal: true
          }));
        }
      }
    };

    const interval = setInterval(checkForRepeatedFailures, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [requestMetrics]);

  // Clean old metrics (keep only last 50 requests)
  useEffect(() => {
    if (requestMetrics.length > 50) {
      setRequestMetrics(prev => prev.slice(-50));
    }
  }, [requestMetrics.length]);

  const dismissBackendErrorModal = () => {
    console.log('🔄 Dismissing backend error modal');
    setBackendStatus({
      isBackendError: false,
      isSlowResponse: false,
      showBackendErrorModal: false
    });
  };

  // Add testing functions to window for easy testing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).testBackendError = () => {
        console.log('🧪 Testing backend error modal');
        triggerBackendErrorModal(false);
      };
      (window as any).testSlowResponse = () => {
        console.log('🧪 Testing slow response modal');
        triggerBackendErrorModal(true);
      };
    }
  }, []);

  return {
    ...backendStatus,
    trackRequest,
    dismissBackendErrorModal,
    triggerBackendErrorModal
  };
};
